/**
 * Privacy-policy consent: persistence + device-language detection.
 *
 * Acceptance is stored with @capacitor/preferences (durable native storage that
 * survives app restarts and clears on uninstall — exactly the "show once per
 * install" semantics we want; on the web it falls back to localStorage). The
 * stored value is the boolean flag `privacyPolicyAccepted = "true"`.
 *
 * Mirrors the storage approach already used for the refresh token in
 * `lib/api-client.ts`, so there is one consistent persistence mechanism.
 */
import { Preferences } from "@capacitor/preferences";
import { Device } from "@capacitor/device";

/** Languages the Privacy Policy / consent flow supports. */
export type PolicyLang = "en" | "zh";

/** Preferences key holding the consent flag. */
export const PRIVACY_ACCEPTED_KEY = "privacyPolicyAccepted";

/** True once the user has accepted the Privacy Policy on this install. */
export async function getPrivacyAccepted(): Promise<boolean> {
  try {
    const { value } = await Preferences.get({ key: PRIVACY_ACCEPTED_KEY });
    return value === "true";
  } catch {
    // Storage unavailable — fail closed so the consent screen is shown.
    return false;
  }
}

/** Persist the consent decision. Defaults to accepted; pass `false` to reset. */
export async function setPrivacyAccepted(accepted = true): Promise<void> {
  try {
    if (accepted) {
      await Preferences.set({ key: PRIVACY_ACCEPTED_KEY, value: "true" });
    } else {
      await Preferences.remove({ key: PRIVACY_ACCEPTED_KEY });
    }
  } catch {
    /* best-effort — nothing else to do if storage is disabled */
  }
}

/**
 * Best-effort device language → policy language. Any Chinese locale (`zh`,
 * `zh-CN`, `zh-Hans`, …) maps to the Simplified-Chinese policy; everything else
 * defaults to English, per the requirement.
 */
export async function getDevicePolicyLang(): Promise<PolicyLang> {
  try {
    const { value } = await Device.getLanguageCode(); // e.g. "en", "zh"
    return value?.toLowerCase().startsWith("zh") ? "zh" : "en";
  } catch {
    return "en";
  }
}
